import { ErrorResponse, ListPayload } from 'core/http/response';
import { Reducer } from 'redux';
import { CustomerActions } from './actionType';
import { Customer } from './customer';

interface CustomerEntities {
    [key: string]: Customer;
}
export interface CustomersState {
    isLoading: boolean;
    error?: ErrorResponse;
    customers: ListPayload<Customer>;
    entities: CustomerEntities;
    jobsCounts: Record<string, number>;
    invoicesCounts: Record<string, number>;
}

export const customersInitialState: Readonly<CustomersState> = {
    isLoading: false,
    customers: {
        list: [],
        pageNumber: 0,
        pageSize: 0,
        total: 0,
    },
    entities: {},
    jobsCounts: {},
    invoicesCounts: {},
};

const customers: Reducer<CustomersState, CustomerActions> = (state = customersInitialState, action): CustomersState => {
    switch (action.type) {
        case 'LIST_CUSTOMERS_REQUEST':
            return {
                ...state,
                isLoading: true,
                error: undefined,
            };
        case 'LIST_CUSTOMERS_SUCCESS': {
            const incoming = action.payload.list.reduce<Record<string, Customer>>((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, {});

            return {
                ...state,
                isLoading: false,
                error: undefined,
                customers: action.payload,
                entities: { ...state.entities, ...incoming },
            };
        }
        case 'LIST_CUSTOMERS_ERROR':
            return {
                ...state,
                isLoading: false,
                customers: state.customers,
                error: action.error,
            };
        case 'CREATE_CUSTOMER_REQUEST':
            return {
                ...state,
                isLoading: true,
                error: undefined,
            };
        case 'CREATE_CUSTOMER_SUCCESS':
            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: { ...state.entities, [action.payload.id]: action.payload },
            };
        case 'CREATE_CUSTOMER_ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.error,
            };
        case 'GET_CUSTOMER_REQUEST':
            return {
                ...state,
                isLoading: true,
                error: undefined,
            };
        case 'GET_CUSTOMER_SUCCESS':
            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: { ...state.entities, [action.payload.id]: action.payload },
            };
        case 'GET_CUSTOMER_ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.error,
            };
        case 'EDIT_CUSTOMER_REQUEST':
            return {
                ...state,
                isLoading: true,
                error: undefined,
            };
        case 'EDIT_CUSTOMER_SUCCESS':
            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: { ...state.entities, [action.payload.id]: action.payload },
            };
        case 'EDIT_CUSTOMER_ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.error,
            };
        case 'DELETE_CUSTOMER_REQUEST':
            return {
                ...state,
                error: undefined,
                isLoading: true,
            };
        case 'DELETE_CUSTOMER_SUCCESS': {
            const { [action.payload.id]: _removed, ...rest } = state.entities;

            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: rest,
                customers: {
                    ...state.customers,
                    list: state.customers.list.filter((c) => c.id !== action.payload.id),
                    total: Math.max(0, state.customers.total - 1),
                },
            };
        }
        case 'DELETE_CUSTOMER_ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.error,
            };
        case 'GET_CUSTOMER_JOBS_COUNT_SUCCESS': {
            const { customerId, count } = action.payload;
            return {
                ...state,
                jobsCounts: { ...state.jobsCounts, [customerId]: count },
            };
        }
        case 'GET_CUSTOMER_INVOICES_COUNT_SUCCESS': {
            const { customerId, count } = action.payload;
            return {
                ...state,
                invoicesCounts: { ...state.invoicesCounts, [customerId]: count },
            };
        }
        default:
            return state;
    }
};

export default customers;
